/**
 * AssistantApplyPills — Spec 64 Phase A + Phase D
 *
 * Renders one pill per field from the latest assistant apply-block, with an
 * individual checkbox (default checked) and an "Apply selected" button.
 *
 * Per feedback_selective_assistant_apply.md: every row MUST be individually
 * toggleable — not all-or-nothing.
 *
 * Block parsing is in `./parseApplyBlock` so it can be unit-tested
 * independently of React (Spec 64 Phase D).
 */
import { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { Button } from '../ui/Button.js';
import { parseApplyBlock } from './parseApplyBlock.js';
import { Toggle } from '../ui/Toggle.js';

function previewValue(val: unknown): string {
  if (val === null || val === undefined) return '(empty)';
  if (typeof val === 'string') return val.length > 60 ? `${val.slice(0, 57)}…` : val;
  return JSON.stringify(val).slice(0, 60);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface AssistantApplyPillsProps<FormValues extends Record<string, unknown>> {
  /** Latest assistant message content — parsed for apply blocks */
  latestMessage: string;
  onApplyFieldUpdate: (partial: Partial<FormValues>) => void;
}

export function AssistantApplyPills<FormValues extends Record<string, unknown>>({
  latestMessage,
  onApplyFieldUpdate,
}: AssistantApplyPillsProps<FormValues>) {
  const parsed = parseApplyBlock(latestMessage);
  const fields = parsed ? Object.keys(parsed) : [];

  // Track checked state per field (all checked by default)
  const [checked, setChecked] = useState<Record<string, boolean>>({});

  // Re-init when a new apply block is parsed
  useEffect(() => {
    if (!parsed) return;
    const init: Record<string, boolean> = {};
    for (const key of Object.keys(parsed)) {
      init[key] = true;
    }
    setChecked(init);
  }, [latestMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!parsed || fields.length === 0) return null;

  const toggleField = (key: string) => {
    setChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const checkedCount = fields.filter((k) => checked[k]).length;

  const handleApply = () => {
    const partial: Partial<FormValues> = {};
    for (const key of fields) {
      if (checked[key]) {
        (partial as Record<string, unknown>)[key] = parsed[key];
      }
    }
    if (Object.keys(partial).length > 0) {
      onApplyFieldUpdate(partial);
    }
  };

  return (
    <div className="mt-2 rounded-lg border border-card-border bg-background-gray-secondary_alt p-3">
      <p className="mb-2 text-xs font-medium text-text-tertiary">Suggested updates — select to apply:</p>
      <div className="space-y-1.5">
        {fields.map((key) => (
          <div key={key} className="flex items-start gap-2 rounded-sm p-1.5 hover:bg-background-gray-secondary">
            <Toggle
              size="sm"
              checked={checked[key] ?? true}
              onChange={() => toggleField(key)}
              aria-label={`Apply ${key}`}
            />
            <span className="min-w-0 flex-1">
              <span className="text-xs font-medium text-title-50">{key}</span>
              <span className="ml-1.5 text-xs text-text-tertiary">{previewValue(parsed[key])}</span>
            </span>
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span className="text-xs text-text-tertiary">
          {checkedCount} of {fields.length} selected
        </span>
        <Button size="sm" variant="primary" disabled={checkedCount === 0} onClick={handleApply}>
          <Check size={12} />
          Apply {checkedCount > 0 ? `(${checkedCount})` : ''}
        </Button>
      </div>
    </div>
  );
}
